import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'

import { getSelectedBusinessId } from 'data/select-account/selectAccount'
import { getCustomerDetailsPageUrl } from 'router/routes'
import { captureError } from 'utils/error'

import DemoCallMenu from './DemoCallMenu'
import { CallerMatch, createSession, lookupCaller } from './screenPopApi'
import ScreenPopPanel from './ScreenPopPanel'

import type { DialStackInstance, IncomingCallEvent } from '@dialstack/sdk-js'

const REPEAT_WINDOW_MS = 15000

interface IncomingCall {
  fromNumber: string
  fromName: string | null
  matches?: CallerMatch[]
}

const ScreenPop = () => {
  const businessId: string = useSelector(getSelectedBusinessId)
  const navigate = useNavigate()
  const [instance, setInstance] = useState<DialStackInstance | null>(null)
  const [hasDemoControls, setHasDemoControls] = useState(false)
  const [call, setCall] = useState<IncomingCall | null>(null)
  const lastPopRef = useRef<{ fromNumber: string; at: number } | null>(null)

  const handleClose = useCallback(() => setCall(null), [])

  const handleOpenCustomer = useCallback((accountId: string) => navigate(getCustomerDetailsPageUrl(accountId)), [navigate])

  useEffect(() => {
    if (!businessId) {
      return undefined
    }
    let isCancelled = false
    let dialstack: DialStackInstance | undefined

    const handleIncomingCall = async (event: IncomingCallEvent) => {
      const last = lastPopRef.current
      if (last && last.fromNumber === event.from_number && Date.now() - last.at < REPEAT_WINDOW_MS) {
        return
      }
      lastPopRef.current = { fromNumber: event.from_number, at: Date.now() }
      const caller = { fromNumber: event.from_number, fromName: event.from_name }
      setCall(caller)
      try {
        const matches = await lookupCaller(businessId, event.from_number)
        setCall({ ...caller, matches })
        if (matches.length === 1) {
          navigate(getCustomerDetailsPageUrl(matches[0].accountId))
        }
      } catch (error) {
        captureError(error)
        setCall({ ...caller, matches: [] })
      }
    }

    const start = async () => {
      const session = await createSession(businessId)
      if (!session || isCancelled) {
        return
      }
      const { loadDialstackAndInitialize } = await import('@dialstack/sdk-js')
      let firstSession: typeof session | null = session
      dialstack = await loadDialstackAndInitialize({
        publishableKey: session.publishableKey,
        appInfo: { name: 'crm-core-web' },
        fetchClientSecret: async () => {
          const next = firstSession ?? (await createSession(businessId))
          firstSession = null
          return { clientSecret: next?.clientSecret ?? '', expiresAt: next?.expiresAt }
        },
      })
      if (isCancelled) {
        dialstack.logout()
        return
      }
      dialstack.on('call.incoming', handleIncomingCall)
      setInstance(dialstack)
      setHasDemoControls(session.demoControls)
    }

    start().catch(captureError)

    return () => {
      isCancelled = true
      dialstack?.logout()
      setInstance(null)
      setHasDemoControls(false)
      setCall(null)
    }
  }, [businessId, navigate])

  if (!instance) {
    return null
  }

  return (
    <>
      {hasDemoControls && <DemoCallMenu businessId={businessId} />}
      {call && (
        <ScreenPopPanel
          fromName={call.fromName}
          fromNumber={call.fromNumber}
          instance={instance}
          matches={call.matches}
          onClose={handleClose}
          onOpenCustomer={handleOpenCustomer}
        />
      )}
    </>
  )
}

export default ScreenPop
